import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { brl } from "@/lib/format";
import { Plus, Building2, DollarSign, Users, Ban, CheckCircle2, Copy, Loader2, Zap, MessageCircle, Pencil, Trash2, X, Receipt, FileText, TestTube, ExternalLink, Search, Printer, Download } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { lookupCep, maskCep } from "@/lib/viacep";

export default function PlatformAdmin() {
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(null);
  const [form, setForm] = useState({
    name: "", slug: "", owner_name: "", owner_email: "", owner_password: "",
    owner_cpf_cnpj: "", owner_phone: "", monthly_fee: "0", payment_day: "10",
    auto_bill: true,
    owner_cep: "", owner_address_street: "", owner_address_number: "",
    owner_address_complement: "", owner_neighborhood: "", owner_city: "", owner_state: "",
    max_users: "", max_professionals: "",
  });
  const [newCepLoading, setNewCepLoading] = useState(false);
  const [editCepLoading, setEditCepLoading] = useState(false);

  // Autofill de CEP para o formulário de Nova franquia usando ViaCEP.
  const newDoCep = async () => {
    const d = (form.owner_cep || "").replace(/\D/g, "");
    if (d.length !== 8) { toast.error("Informe um CEP com 8 dígitos"); return; }
    setNewCepLoading(true);
    try {
      const r = await lookupCep(d);
      if (!r) { toast.error("CEP não encontrado"); return; }
      setForm((f) => ({
        ...f, owner_cep: maskCep(d),
        owner_address_street: r.street || f.owner_address_street,
        owner_neighborhood: r.neighborhood || f.owner_neighborhood,
        owner_city: r.city || f.owner_city,
        owner_state: r.state || f.owner_state,
      }));
      toast.success("Endereço preenchido");
    } finally { setNewCepLoading(false); }
  };
  // Mesma coisa mas para o dialog de Edição.
  const editDoCep = async () => {
    const d = (editForm.owner_cep || "").replace(/\D/g, "");
    if (d.length !== 8) { toast.error("Informe um CEP com 8 dígitos"); return; }
    setEditCepLoading(true);
    try {
      const r = await lookupCep(d);
      if (!r) { toast.error("CEP não encontrado"); return; }
      setEditForm((f) => ({
        ...f, owner_cep: maskCep(d),
        owner_address_street: r.street || f.owner_address_street,
        owner_neighborhood: r.neighborhood || f.owner_neighborhood,
        owner_city: r.city || f.owner_city,
        owner_state: r.state || f.owner_state,
      }));
      toast.success("Endereço preenchido");
    } finally { setEditCepLoading(false); }
  };
  const [payForm, setPayForm] = useState({ amount: "", period: new Date().toISOString().slice(0, 7), note: "" });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [invTenant, setInvTenant] = useState(null); // tenant whose invoices we're managing
  const [invList, setInvList] = useState([]);
  const [invForm, setInvForm] = useState({ period: "", amount: "", due_date: "", note: "" });
  const [invIssueNfe, setInvIssueNfe] = useState(false);
  const [creating, setCreating] = useState(false);
  const [subLoading, setSubLoading] = useState(null);

  const load = async () => setTenants((await api.get("/platform/tenants")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const payload = { ...form, monthly_fee: Number(form.monthly_fee) };
      payload.max_users = form.max_users ? Number(form.max_users) : null;
      payload.max_professionals = form.max_professionals ? Number(form.max_professionals) : null;
      payload.payment_day = form.payment_day ? Math.min(28, Math.max(1, Number(form.payment_day))) : null;
      payload.auto_bill = !!form.auto_bill;
      await api.post("/platform/tenants", payload);
      toast.success("Franquia criada");
      setOpen(false); setForm({
        name: "", slug: "", owner_name: "", owner_email: "", owner_password: "",
        owner_cpf_cnpj: "", owner_phone: "", monthly_fee: "0", payment_day: "10",
        auto_bill: true,
        owner_cep: "", owner_address_street: "", owner_address_number: "",
        owner_address_complement: "", owner_neighborhood: "", owner_city: "", owner_state: "",
        max_users: "", max_professionals: "",
      });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
    finally { setCreating(false); }
  };

  const toggleActive = async (t) => {
    await api.patch(`/platform/tenants/${t.id}`, { active: !t.active });
    toast.success(t.active ? "Franquia desativada" : "Franquia ativada");
    load();
  };
  const updateFee = async (t, fee) => {
    await api.patch(`/platform/tenants/${t.id}`, { monthly_fee: Number(fee) });
    toast.success("Mensalidade atualizada");
    load();
  };
  const registerPayment = async (t) => {
    if (!payForm.amount) return;
    await api.post(`/platform/tenants/${t.id}/payment`, { amount: Number(payForm.amount), period: payForm.period, note: payForm.note });
    toast.success("Pagamento registrado");
    setPayOpen(null); setPayForm({ amount: "", period: new Date().toISOString().slice(0, 7), note: "" });
    load();
  };
  const copyBookingLink = (slug) => {
    const url = `${window.location.origin}/agendar/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado");
  };

  const activateSubscription = async (t) => {
    if (!t.owner_cpf_cnpj) { toast.error("Cadastre o CPF/CNPJ do dono antes"); return; }
    if (!window.confirm(`Criar assinatura Asaas mensal de ${brl(t.monthly_fee)} para ${t.name}?`)) return;
    setSubLoading(t.id);
    try {
      await api.post(`/platform/tenants/${t.id}/subscription`);
      toast.success("Assinatura Asaas criada!");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.provider_error?.errors?.[0]?.description || "Falha ao criar assinatura");
    } finally { setSubLoading(null); }
  };

  const cancelSubscription = async (t) => {
    if (!window.confirm(`Cancelar assinatura Asaas de ${t.name}?`)) return;
    setSubLoading(t.id);
    try { await api.delete(`/platform/tenants/${t.id}/subscription`); toast.success("Assinatura cancelada"); load(); }
    catch { toast.error("Falha"); }
    finally { setSubLoading(null); }
  };

  const sendDueReminder = (t) => {
    const phone = (t.owner_phone || "").replace(/\D/g, "");
    const dueLabel = t.next_due_date ? new Date(t.next_due_date + "T00:00:00").toLocaleDateString("pt-BR") : "";
    const msg = encodeURIComponent(
      `Olá ${t.owner_name}! Lembrete da mensalidade da sua agenda:\n\n` +
      `💰 Valor: ${brl(t.monthly_fee)}\n` +
      `📅 Vencimento: ${dueLabel}\n\n` +
      `Efetue o pagamento até a data para manter o acesso ativo. Obrigado!`
    );
    const url = phone.length >= 10 ? `https://wa.me/55${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, "_blank");
  };

  const saveDomain = async (t, domain) => {
    try { await api.patch(`/platform/tenants/${t.id}`, { custom_domain: domain || null }); toast.success("Domínio atualizado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  const openInvoices = async (t) => {
    setInvTenant(t);
    const now = new Date();
    const period = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const due = new Date(now.getFullYear(), now.getMonth(), 10).toISOString().slice(0, 10);
    setInvForm({ period, amount: String(t.monthly_fee || 0), due_date: due, note: "" });
    try {
      const { data } = await api.get(`/platform/tenants/${t.id}/invoices`);
      setInvList(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao carregar"); }
  };

  const reloadInvoices = async () => {
    if (!invTenant) return;
    const { data } = await api.get(`/platform/tenants/${invTenant.id}/invoices`);
    setInvList(data || []);
  };

  const createInvoice = async () => {
    if (!invForm.period || !invForm.due_date) { toast.error("Preencha período e vencimento"); return; }
    try {
      await api.post(`/platform/tenants/${invTenant.id}/invoices`, {
        period: invForm.period,
        amount: Number(invForm.amount || 0),
        due_date: invForm.due_date,
        note: invForm.note || "",
      });
      toast.success("Fatura criada");
      setInvForm({ ...invForm, note: "" });
      reloadInvoices();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  const invAction = async (iid, path, msg, opts = {}) => {
    try {
      // The charge endpoint accepts `?issue_invoice=true` — we forward it
      // when the platform admin ticked "Emitir NF-e junto" for THIS invoice.
      const qs = opts.issue_nfe ? "?issue_invoice=true" : "";
      const { data } = await api.post(`/platform/tenants/${invTenant.id}/invoices/${iid}/${path}${qs}`);
      toast.success(msg);
      if (data?.nfe) {
        if (data.nfe.issued) toast.success("NF-e agendada no Asaas");
        else toast.warning(data.nfe.detail || "NF-e não emitida — verifique no Asaas");
      }
      reloadInvoices();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  const deleteInvoice = async (iid) => {
    if (!window.confirm("Excluir esta fatura?")) return;
    try {
      await api.delete(`/platform/tenants/${invTenant.id}/invoices/${iid}`);
      toast.success("Fatura excluída");
      reloadInvoices();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  const openEdit = (t) => {
    setEditing(t);
    setEditForm({
      name: t.name || "",
      slug: t.slug || "",
      owner_email: t.owner_email || "",
      monthly_fee: String(t.monthly_fee || 0),
      payment_day: String(t.payment_day || 10),
      auto_bill: t.auto_bill !== false, // default true
      owner_phone: t.owner_phone || "",
      owner_cpf_cnpj: t.owner_cpf_cnpj || "",
      owner_cep: t.owner_cep || "",
      owner_address_street: t.owner_address_street || "",
      owner_address_number: t.owner_address_number || "",
      owner_address_complement: t.owner_address_complement || "",
      owner_neighborhood: t.owner_neighborhood || "",
      owner_city: t.owner_city || "",
      owner_state: t.owner_state || "",
      notes: t.notes || "",
      max_users: t.max_users == null ? "" : String(t.max_users),
      max_professionals: t.max_professionals == null ? "" : String(t.max_professionals),
    });
  };

  const saveEdit = async () => {
    try {
      // Empty string → -1 sentinel so backend resets to unlimited.
      const parseCap = (v) => (v === "" ? -1 : Number(v));
      const payload = {
        name: editForm.name,
        monthly_fee: Number(editForm.monthly_fee),
        payment_day: editForm.payment_day ? Math.min(28, Math.max(1, Number(editForm.payment_day))) : null,
        auto_bill: !!editForm.auto_bill,
        owner_phone: editForm.owner_phone,
        owner_cpf_cnpj: editForm.owner_cpf_cnpj,
        owner_cep: editForm.owner_cep,
        owner_address_street: editForm.owner_address_street,
        owner_address_number: editForm.owner_address_number,
        owner_address_complement: editForm.owner_address_complement,
        owner_neighborhood: editForm.owner_neighborhood,
        owner_city: editForm.owner_city,
        owner_state: editForm.owner_state,
        notes: editForm.notes,
        max_users: parseCap(editForm.max_users),
        max_professionals: parseCap(editForm.max_professionals),
      };
      // Send slug/email only when the admin actually changed them, to avoid
      // running the uniqueness check on the current value.
      if (editForm.slug && editForm.slug !== editing.slug) payload.slug = editForm.slug;
      if (editForm.owner_email && editForm.owner_email !== editing.owner_email) payload.owner_email = editForm.owner_email;
      await api.patch(`/platform/tenants/${editing.id}`, payload);
      toast.success("Franquia atualizada");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  const deleteTenant = async (t, hard = false) => {
    try {
      await api.delete(`/platform/tenants/${t.id}${hard ? "?hard=true" : ""}`);
      toast.success(hard ? "Franquia excluída permanentemente" : "Franquia desativada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Franquias</h1>
          <p className="text-sm text-muted-foreground mt-1">Painel do super-administrador. Gerencie franqueados e mensalidades.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-new-tenant" className="h-11"><Plus className="h-4 w-4 mr-2" /> Nova franquia</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nova franquia</DialogTitle><DialogDescription>Cadastre uma nova franquia, o login do dono e (opcional) limites de usuários e profissionais.</DialogDescription></DialogHeader>
            <div className="grid gap-4">
              <div className="space-y-2"><Label>Nome da franquia / estabelecimento</Label><Input data-testid="t-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex.: Oficina do Zé" /></div>
              <div className="space-y-2"><Label>Slug (URL pública)</Label><Input data-testid="t-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="oficina-do-ze" /><p className="text-xs text-muted-foreground">Link ficará: <code>{window.location.origin}/agendar/{form.slug || "oficina-do-ze"}</code></p></div>
              <div className="surface-muted p-3 text-xs space-y-1">
                <div className="font-medium">O que acontece ao criar:</div>
                <div>• O dono recebe uma conta <strong>master</strong> no próprio sistema (permissões completas)</div>
                <div>• Login pelo mesmo domínio (<code>{window.location.origin}/login</code>) com o email e senha abaixo</div>
                <div>• Página pública já ativa em <code>/agendar/{form.slug || "slug"}</code> — nada precisa de DNS ou domínio próprio</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Nome do dono</Label><Input data-testid="t-owner-name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} /></div>
                <div className="space-y-2"><Label>Mensalidade (R$)</Label><Input data-testid="t-fee" type="number" min="0" step="0.01" value={form.monthly_fee} onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Dia da cobrança (1-28)</Label><Input data-testid="t-pay-day" type="number" min="1" max="28" value={form.payment_day} onChange={(e) => setForm({ ...form, payment_day: e.target.value })} placeholder="Ex.: 10" /><p className="text-[11px] text-muted-foreground">Usado pela assinatura Asaas e pela geração automática de faturas.</p></div>
                <div className="space-y-2"><Label>Email do dono (login)</Label><Input data-testid="t-owner-email" type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} /></div>
              </div>
              <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none border border-border rounded-lg p-3 bg-muted/30" data-testid="toggle-t-auto-bill">
                <input
                  type="checkbox"
                  checked={!!form.auto_bill}
                  onChange={(e) => setForm({ ...form, auto_bill: e.target.checked })}
                  className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
                />
                <span><strong className="text-foreground">Repetir cobrança automaticamente todo mês</strong><span className="block text-[10px]">Cria a fatura + cobrança PIX Asaas 5 dias antes do dia acima, todo mês, aparecendo já paga-vel no painel do franqueado.</span></span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Senha inicial (mín. 6)</Label><Input data-testid="t-owner-password" type="password" minLength={6} value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} /></div>
                <div className="space-y-2"><Label>CPF/CNPJ do dono</Label><Input data-testid="t-owner-cpf" value={form.owner_cpf_cnpj} onChange={(e) => setForm({ ...form, owner_cpf_cnpj: e.target.value })} placeholder="p/ cobrança automática" /></div>
              </div>
              <div className="space-y-2"><Label>Telefone do dono</Label><Input data-testid="t-owner-phone" value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} placeholder="(27) 99999-0000" /></div>

              {/* Endereço da franquia — obrigatório no Asaas para emitir NF-e da mensalidade. */}
              <div className="surface-muted p-3 space-y-3">
                <div className="text-xs font-medium">Endereço da franquia (para NF-e da mensalidade)</div>
                <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
                  <div className="space-y-2">
                    <Label>CEP</Label>
                    <Input
                      data-testid="t-cep"
                      value={form.owner_cep}
                      onChange={(e) => setForm({ ...form, owner_cep: maskCep(e.target.value) })}
                      onBlur={() => { if ((form.owner_cep || "").replace(/\D/g, "").length === 8) newDoCep(); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); newDoCep(); } }}
                      placeholder="00000-000"
                      inputMode="numeric"
                    />
                  </div>
                  <Button type="button" variant="outline" onClick={newDoCep} disabled={newCepLoading} data-testid="t-cep-lookup" className="h-10">
                    {newCepLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1" /> Buscar</>}
                  </Button>
                </div>
                <div className="grid grid-cols-[1fr_100px] gap-3">
                  <div className="space-y-2"><Label>Rua/Logradouro</Label><Input data-testid="t-street" value={form.owner_address_street} onChange={(e) => setForm({ ...form, owner_address_street: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Número</Label><Input data-testid="t-number" value={form.owner_address_number} onChange={(e) => setForm({ ...form, owner_address_number: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Complemento</Label><Input data-testid="t-complement" value={form.owner_address_complement} onChange={(e) => setForm({ ...form, owner_address_complement: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Bairro</Label><Input data-testid="t-neigh" value={form.owner_neighborhood} onChange={(e) => setForm({ ...form, owner_neighborhood: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-[1fr_90px] gap-3">
                  <div className="space-y-2"><Label>Cidade</Label><Input data-testid="t-city" value={form.owner_city} onChange={(e) => setForm({ ...form, owner_city: e.target.value })} /></div>
                  <div className="space-y-2"><Label>UF</Label><Input data-testid="t-state" maxLength={2} value={form.owner_state} onChange={(e) => setForm({ ...form, owner_state: e.target.value.toUpperCase() })} /></div>
                </div>
              </div>

              <div className="surface-muted p-3 space-y-3">
                <div className="text-xs font-medium">Limites da franquia (opcional)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Máx. usuários</Label><Input data-testid="t-max-users" type="number" min="1" value={form.max_users} onChange={(e) => setForm({ ...form, max_users: e.target.value })} placeholder="Ilimitado" /></div>
                  <div className="space-y-2"><Label>Máx. profissionais</Label><Input data-testid="t-max-pros" type="number" min="1" value={form.max_professionals} onChange={(e) => setForm({ ...form, max_professionals: e.target.value })} placeholder="Ilimitado" /></div>
                </div>
                <p className="text-[11px] text-muted-foreground">Deixe em branco para não limitar. Ao atingir o limite, o franqueado receberá erro ao cadastrar mais.</p>
              </div>
            </div>
            <DialogFooter><Button data-testid="btn-save-tenant" onClick={create} disabled={creating}>{creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Criar franquia</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="surface p-4"><div className="text-xs uppercase text-muted-foreground">Franquias</div><div className="text-2xl font-display font-bold">{tenants.length}</div></div>
        <div className="surface p-4"><div className="text-xs uppercase text-muted-foreground">Ativas</div><div className="text-2xl font-display font-bold">{tenants.filter((t) => t.active).length}</div></div>
        <div className="surface p-4"><div className="text-xs uppercase text-muted-foreground">MRR (mensalidades)</div><div className="text-2xl font-display font-bold">{brl(tenants.filter((t) => t.active).reduce((s, t) => s + Number(t.monthly_fee || 0), 0))}</div></div>
      </div>

      <div className="surface divide-y divide-border">
        {tenants.map((t) => (
          <div key={t.id} data-testid={`tenant-row-${t.id}`} className="p-4 flex flex-wrap items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Building2 className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-medium flex items-center gap-2">
                {t.name} {t.slug === "master" && <span className="text-xs text-accent">(master)</span>}
                {t.overdue && <span className="text-xs bg-destructive/10 text-destructive border border-destructive/30 px-2 py-0.5 rounded" data-testid={`overdue-${t.id}`}>{t.days_since_payment}d atraso</span>}
              </div>
              <div className="text-xs text-muted-foreground">/agendar/{t.slug} · {t.owner_email}</div>
              <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2" data-testid={`usage-${t.id}`}>
                <span>Usuários: <span className={t.max_users && t.users_count >= t.max_users ? "text-destructive font-medium" : "text-foreground"}>{t.users_count ?? 0}{t.max_users ? ` / ${t.max_users}` : " / ∞"}</span></span>
                <span>· Profissionais: <span className={t.max_professionals && t.professionals_count >= t.max_professionals ? "text-destructive font-medium" : "text-foreground"}>{t.professionals_count ?? 0}{t.max_professionals ? ` / ${t.max_professionals}` : " / ∞"}</span></span>
              </div>
              {t.asaas_subscription_id && <div className="text-xs text-primary inline-flex items-center gap-1"><Zap className="h-3 w-3" /> Assinatura Asaas ativa</div>}
              {t.last_payment_at && <div className="text-xs text-primary">Último pgto: {new Date(t.last_payment_at).toLocaleDateString("pt-BR")} ({t.days_since_payment}d atrás)</div>}
              {t.next_due_date && !t.overdue && <div className="text-xs text-muted-foreground">Próx. venc.: {new Date(t.next_due_date + "T00:00:00").toLocaleDateString("pt-BR")} (em {t.days_until_due}d)</div>}
            </div>
            <div className="w-32">
              <Input data-testid={`fee-${t.id}`} key={`fee-${t.id}-${t.monthly_fee}`} type="number" min="0" step="0.01" defaultValue={t.monthly_fee || 0} className="h-9" onBlur={(e) => updateFee(t, e.target.value)} />
            </div>
            <Button variant="outline" size="sm" onClick={() => copyBookingLink(t.slug)} data-testid={`copy-slug-${t.id}`} title="Copiar link"><Copy className="h-4 w-4" /></Button>
            {t.slug !== "master" && (
              <>
                <Button variant="outline" size="sm" onClick={() => sendDueReminder(t)} data-testid={`wa-remind-${t.id}`} title="Enviar lembrete WhatsApp"><MessageCircle className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => openInvoices(t)} data-testid={`invoices-${t.id}`} title="Faturas"><Receipt className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => openEdit(t)} data-testid={`edit-tenant-${t.id}`} title="Editar franquia"><Pencil className="h-4 w-4" /></Button>
                {!t.asaas_subscription_id ? (
                  <Button variant="outline" size="sm" onClick={() => activateSubscription(t)} disabled={subLoading === t.id} data-testid={`sub-activate-${t.id}`} title="Ativar assinatura Asaas">
                    {subLoading === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => cancelSubscription(t)} disabled={subLoading === t.id} data-testid={`sub-cancel-${t.id}`} title="Cancelar assinatura" className="text-destructive">
                    {subLoading === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                  </Button>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid={`delete-tenant-${t.id}`} title="Excluir franquia" className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Excluir franquia "{t.name}"?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Isso apagará <strong>permanentemente</strong> todos os dados desta franquia: usuários, serviços, agendamentos, clientes, cobranças e configurações. Esta ação não pode ser desfeita.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={() => deleteTenant(t, true)} className="bg-destructive hover:bg-destructive/90">Excluir tudo</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
            <div className="flex items-center gap-2 text-xs" data-testid={`status-${t.id}`}>
              <Switch checked={t.active} onCheckedChange={() => toggleActive(t)} />
              {t.active ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <Ban className="h-4 w-4 text-destructive" />}
            </div>
            <Dialog open={payOpen === t.id} onOpenChange={(o) => setPayOpen(o ? t.id : null)}>
              <DialogTrigger asChild><Button variant="outline" size="sm" data-testid={`btn-pay-tenant-${t.id}`}><DollarSign className="h-4 w-4 mr-1" /> Cobrar</Button></DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>Registrar pagamento — {t.name}</DialogTitle><DialogDescription>Informe o valor recebido e a data de referência para lançamento manual.</DialogDescription></DialogHeader>
                <div className="grid gap-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2"><Label>Valor (R$)</Label><Input data-testid="pay-amount" type="number" min="0" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Período (AAAA-MM)</Label><Input data-testid="pay-period" type="month" value={payForm.period} onChange={(e) => setPayForm({ ...payForm, period: e.target.value })} /></div>
                  </div>
                  <div className="space-y-2"><Label>Observação</Label><Textarea data-testid="pay-note" rows={2} value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} placeholder="Ex.: Pago via PIX em 05/08" /></div>
                  {t.payments?.length > 0 && (
                    <div className="surface-muted p-3 max-h-40 overflow-y-auto text-xs space-y-1">
                      {t.payments.slice().reverse().map((p, i) => <div key={i} className="flex justify-between"><span>{p.period}</span><span>{brl(p.amount)}</span></div>)}
                    </div>
                  )}
                </div>
                <DialogFooter><Button data-testid="btn-register-payment" onClick={() => registerPayment(t)} disabled={!payForm.amount}>Registrar</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        ))}
        {tenants.length === 0 && <div className="p-12 text-center text-muted-foreground">Nenhuma franquia. Crie a primeira.</div>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar franquia — {editing?.name}</DialogTitle><DialogDescription>Atualize dados cadastrais, mensalidade e limites de uso da franquia.</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div className="space-y-2"><Label>Nome da franquia</Label><Input data-testid="edit-t-name" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Mensalidade (R$)</Label><Input data-testid="edit-t-fee" type="number" min="0" step="0.01" value={editForm.monthly_fee || "0"} onChange={(e) => setEditForm({ ...editForm, monthly_fee: e.target.value })} /></div>
              <div className="space-y-2"><Label>Telefone do dono</Label><Input data-testid="edit-t-phone" value={editForm.owner_phone || ""} onChange={(e) => setEditForm({ ...editForm, owner_phone: e.target.value })} placeholder="(27) 99999-8888" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Dia da cobrança (1-28)</Label><Input data-testid="edit-t-pay-day" type="number" min="1" max="28" value={editForm.payment_day || ""} onChange={(e) => setEditForm({ ...editForm, payment_day: e.target.value })} placeholder="Ex.: 10" /><p className="text-[11px] text-muted-foreground">Ao alterar, cancele e recrie a assinatura Asaas para que o novo dia entre em vigor.</p></div>
              <div className="space-y-2"><Label>CPF/CNPJ do dono</Label><Input data-testid="edit-t-cpf" value={editForm.owner_cpf_cnpj || ""} onChange={(e) => setEditForm({ ...editForm, owner_cpf_cnpj: e.target.value })} /></div>
            </div>
            <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none border border-border rounded-lg p-3 bg-muted/30" data-testid="edit-toggle-auto-bill">
              <input
                type="checkbox"
                checked={!!editForm.auto_bill}
                onChange={(e) => setEditForm({ ...editForm, auto_bill: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
              />
              <span><strong className="text-foreground">Repetir cobrança automaticamente todo mês</strong><span className="block text-[10px]">Desmarque se quiser que a fatura seja criada só manualmente daqui pra frente.</span></span>
            </label>
            <div className="surface-muted p-3 space-y-3">
              <div className="text-xs font-medium">Limites (deixe em branco = ilimitado)</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Máx. usuários</Label><Input data-testid="edit-t-max-users" type="number" min="1" value={editForm.max_users || ""} onChange={(e) => setEditForm({ ...editForm, max_users: e.target.value })} placeholder="Ilimitado" /></div>
                <div className="space-y-2"><Label>Máx. profissionais</Label><Input data-testid="edit-t-max-pros" type="number" min="1" value={editForm.max_professionals || ""} onChange={(e) => setEditForm({ ...editForm, max_professionals: e.target.value })} placeholder="Ilimitado" /></div>
              </div>
              <p className="text-[11px] text-muted-foreground">Uso atual: {editing?.users_count ?? "?"} usuário(s) · {editing?.professionals_count ?? "?"} profissional(is).</p>
            </div>
            <div className="space-y-2"><Label>Anotações internas</Label><Textarea data-testid="edit-t-notes" rows={2} value={editForm.notes || ""} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Observações sobre esta franquia" /></div>

            {/* Endereço da franquia (edição) — enviado ao Asaas para NF-e. */}
            <div className="surface-muted p-3 space-y-3">
              <div className="text-xs font-medium">Endereço da franquia (NF-e da mensalidade)</div>
              <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
                <div className="space-y-2">
                  <Label>CEP</Label>
                  <Input
                    data-testid="edit-t-cep"
                    value={editForm.owner_cep || ""}
                    onChange={(e) => setEditForm({ ...editForm, owner_cep: maskCep(e.target.value) })}
                    onBlur={() => { if ((editForm.owner_cep || "").replace(/\D/g, "").length === 8) editDoCep(); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); editDoCep(); } }}
                    placeholder="00000-000"
                    inputMode="numeric"
                  />
                </div>
                <Button type="button" variant="outline" onClick={editDoCep} disabled={editCepLoading} data-testid="edit-t-cep-lookup" className="h-10">
                  {editCepLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1" /> Buscar</>}
                </Button>
              </div>
              <div className="grid grid-cols-[1fr_100px] gap-3">
                <div className="space-y-2"><Label>Rua/Logradouro</Label><Input data-testid="edit-t-street" value={editForm.owner_address_street || ""} onChange={(e) => setEditForm({ ...editForm, owner_address_street: e.target.value })} /></div>
                <div className="space-y-2"><Label>Número</Label><Input data-testid="edit-t-number" value={editForm.owner_address_number || ""} onChange={(e) => setEditForm({ ...editForm, owner_address_number: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Complemento</Label><Input data-testid="edit-t-complement" value={editForm.owner_address_complement || ""} onChange={(e) => setEditForm({ ...editForm, owner_address_complement: e.target.value })} /></div>
                <div className="space-y-2"><Label>Bairro</Label><Input data-testid="edit-t-neigh" value={editForm.owner_neighborhood || ""} onChange={(e) => setEditForm({ ...editForm, owner_neighborhood: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-[1fr_90px] gap-3">
                <div className="space-y-2"><Label>Cidade</Label><Input data-testid="edit-t-city" value={editForm.owner_city || ""} onChange={(e) => setEditForm({ ...editForm, owner_city: e.target.value })} /></div>
                <div className="space-y-2"><Label>UF</Label><Input data-testid="edit-t-state" maxLength={2} value={editForm.owner_state || ""} onChange={(e) => setEditForm({ ...editForm, owner_state: e.target.value.toUpperCase() })} /></div>
              </div>
            </div>

            <div className="surface-muted p-3 space-y-3">
              <div className="text-xs font-medium">Credenciais e endereço público</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Slug público (URL)</Label>
                  <Input data-testid="edit-t-slug" value={editForm.slug || ""} onChange={(e) => setEditForm({ ...editForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} placeholder="minha-franquia" />
                  <p className="text-[11px] text-muted-foreground">/agendar/{editForm.slug || "..."}</p>
                </div>
                <div className="space-y-2">
                  <Label>Email de acesso do dono</Label>
                  <Input data-testid="edit-t-email" type="email" value={editForm.owner_email || ""} onChange={(e) => setEditForm({ ...editForm, owner_email: e.target.value })} placeholder="dono@franquia.com" />
                  <p className="text-[11px] text-muted-foreground">Usado no login do painel do franqueado.</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button data-testid="btn-save-edit-tenant" onClick={saveEdit}>Salvar alterações</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoices manager per tenant */}
      <Dialog open={!!invTenant} onOpenChange={(o) => !o && setInvTenant(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Faturas — {invTenant?.name}</DialogTitle>
            <DialogDescription>Crie, cobre via Asaas, marque como paga, isenta ou de teste.</DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border border-border p-4 space-y-3 bg-muted/20">
            <div className="text-sm font-medium">Nova fatura</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1"><Label className="text-xs">Período (AAAA-MM)</Label><Input data-testid="inv-period" placeholder="2026-08" value={invForm.period} onChange={(e) => setInvForm({ ...invForm, period: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">Valor R$</Label><Input data-testid="inv-amount" type="number" min="0" step="0.01" value={invForm.amount} onChange={(e) => setInvForm({ ...invForm, amount: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">Vencimento</Label><Input data-testid="inv-due" type="date" value={invForm.due_date} onChange={(e) => setInvForm({ ...invForm, due_date: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">Nota (opcional)</Label><Input data-testid="inv-note" placeholder="Ex: promoção" value={invForm.note} onChange={(e) => setInvForm({ ...invForm, note: e.target.value })} /></div>
            </div>
            <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid="toggle-inv-tenant-nfe">
              <input
                type="checkbox"
                checked={invIssueNfe}
                onChange={(e) => setInvIssueNfe(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
              />
              <span>Emitir NF-e junto quando gerar a cobrança <span className="block text-[10px]">(usa a conta Asaas master; configure o Município em Configurações antes)</span></span>
            </label>
            <Button data-testid="inv-create" size="sm" onClick={createInvoice}><Plus className="h-4 w-4 mr-1" /> Criar fatura</Button>
          </div>

          <div className="space-y-2 mt-4">
            {invList.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">Nenhuma fatura ainda.</div>}
            {invList.map((i) => (
              <div key={i.id} data-testid={`inv-row-${i.id}`} className="border border-border rounded-lg p-3 flex flex-wrap items-center gap-3">
                <FileText className="h-5 w-5 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{i.period} · {brl(i.amount)}
                    <span className={`ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${i.status === 'paid' ? 'text-emerald-600 border-emerald-500/30 bg-emerald-500/10' : i.status === 'exempt' ? 'text-sky-600 border-sky-500/30 bg-sky-500/10' : i.status === 'test' ? 'text-muted-foreground border-border' : 'text-amber-600 border-amber-500/30 bg-amber-500/10'}`}>{i.status}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">Vencimento: {new Date(i.due_date + "T00:00:00").toLocaleDateString("pt-BR")}{i.paid_at ? ` · Pago em ${new Date(i.paid_at).toLocaleDateString("pt-BR")} (${i.paid_via || "?"})` : ""}</div>
                  {i.asaas_invoice_url && (
                    <div className="text-xs flex flex-wrap items-center gap-3 mt-1">
                      <a href={i.asaas_invoice_url} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1" data-testid={`inv-view-${i.id}`}>
                        <ExternalLink className="h-3 w-3" /> Ver cobrança
                      </a>
                      <button onClick={() => window.open(i.asaas_invoice_url, "_blank")} className="text-primary inline-flex items-center gap-1" data-testid={`inv-print-${i.id}`} title="Abre a página do Asaas com botão Imprimir">
                        <Printer className="h-3 w-3" /> Imprimir
                      </button>
                      {i.asaas_bank_slip_url && (
                        <a href={i.asaas_bank_slip_url} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1" data-testid={`inv-boleto-${i.id}`}>
                          <Download className="h-3 w-3" /> Boleto PDF
                        </a>
                      )}
                      {i.asaas_pix_copy_paste && (
                        <button onClick={() => { navigator.clipboard.writeText(i.asaas_pix_copy_paste); toast.success("PIX copiado"); }} className="text-primary inline-flex items-center gap-1" data-testid={`inv-pix-${i.id}`}>
                          <Copy className="h-3 w-3" /> PIX
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {i.status !== "paid" && (
                  <>
                    {!i.asaas_payment_id && <Button variant="outline" size="sm" onClick={() => invAction(i.id, "charge-asaas", invIssueNfe ? "Cobrança Asaas + NF-e emitidas" : "Cobrança Asaas emitida", { issue_nfe: invIssueNfe })} data-testid={`inv-charge-${i.id}`} title={invIssueNfe ? "Gerar cobrança Asaas + NF-e" : "Gerar cobrança Asaas"}><Zap className="h-4 w-4" /></Button>}
                    <Button variant="outline" size="sm" onClick={() => invAction(i.id, "mark-paid", "Marcada como paga")} data-testid={`inv-paid-${i.id}`} title="Marcar como paga manualmente"><CheckCircle2 className="h-4 w-4 text-emerald-600" /></Button>
                    <Button variant="outline" size="sm" onClick={() => invAction(i.id, "exempt", "Marcada como isenta")} data-testid={`inv-exempt-${i.id}`} title="Isenta"><Ban className="h-4 w-4 text-sky-600" /></Button>
                    <Button variant="outline" size="sm" onClick={() => invAction(i.id, "test", "Marcada como teste")} data-testid={`inv-test-${i.id}`} title="Teste (não conta)"><TestTube className="h-4 w-4" /></Button>
                  </>
                )}
                <Button variant="outline" size="sm" onClick={() => deleteInvoice(i.id)} data-testid={`inv-delete-${i.id}`} title="Excluir" className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvTenant(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
